import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faMagnifyingGlass, faSpinner } from '@fortawesome/free-solid-svg-icons';
import PatientModal from '../Modal/PatientModal';
import PatientImage from './PatientImage';
import { API_BASE_URL } from '../../api';

import './PatientPickerDropdown.scss';

const PAGE_SIZE = 20;

// Standalone patient search + pick list — shows results immediately below the trigger, unlike
// InputPatient/ImprovedSelect whose dropdown only reveals itself once the user clicks into the
// search input. When rendered inside a react-hook-form <Form>, also writes the picked patient's
// id to `name` so the surrounding form submits it like any other field.
export default function PatientPickerDropdown({ onSelect, name, autoFocus = true }) {
  const formMethods = useFormContext();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const listRef = useRef(null);
  const loadingRef = useRef(false);

  const fetchPatients = useCallback(async (keyword, pageNum, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/getPatients`, {
        withCredentials: true,
        params: { page: pageNum, size: PAGE_SIZE, keyword: keyword || undefined },
      });
      const content = data.content || [];
      setResults(prev => (append ? [...prev, ...content] : content));
      setHasMore(!data.last);
      setPage(pageNum);
    } catch (err) {
      console.error('Erreur fetch patients:', err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients('', 0, false);
  }, [fetchPatients]);

  const debouncedSearch = useMemo(() => debounce((keyword) => {
    fetchPatients(keyword, 0, false);
  }, 300), [fetchPatients]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    debouncedSearch(value);
  };

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      fetchPatients(query, page + 1, true);
    }
  };

  const selectPatient = (patient) => {
    if (name) formMethods?.setValue(name, patient?.id ?? null);
    onSelect(patient);
  };

  const handlePatientCreated = (created) => {
    if (!created) return;
    setShowCreate(false);
    selectPatient(created);
  };

  return (
    <div className="patient-picker">
      <div className="patient-picker__search">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="patient-picker__search-icon" />
        <input
          type="text"
          autoFocus={autoFocus}
          placeholder="Rechercher un patient..."
          value={query}
          onChange={handleQueryChange}
        />
      </div>

      <div className="patient-picker__list" ref={listRef} onScroll={handleScroll}>
        {results.map((patient) => (
          <button
            key={patient.id}
            type="button"
            className="patient-picker__item"
            onClick={() => selectPatient(patient)}
          >
            <PatientImage patient={patient} size={28} />
            {patient.fullName}
          </button>
        ))}
        {!loading && results.length === 0 && (
          <p className="patient-picker__empty">Aucun patient trouvé</p>
        )}
        {loading && (
          <div className="patient-picker__loading">
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>
        )}
      </div>

      <button type="button" className="patient-picker__create" onClick={() => setShowCreate(true)}>
        <FontAwesomeIcon icon={faUserPlus} />
        Créer{query ? ` "${query}"` : ' un nouveau patient'}
      </button>

      {showCreate && (
        <PatientModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          patient={(() => {
            const parts = query.trim().split(/\s+/).filter(Boolean);
            if (parts.length === 0) return null;
            return { firstName: parts[0], lastName: parts.slice(1).join(' ') || '' };
          })()}
          onSave={handlePatientCreated}
        />
      )}
    </div>
  );
}
